import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Undo2, X } from 'lucide-react';
import { cn } from '@/lib/format';
import { t } from '@/lib/i18n';

/**
 * Transient confirmations, with an optional undo.
 *
 * Exists for moves: dragging an asset onto the wrong folder is easy and, until
 * now, silent — the asset simply vanished from view with no statement of where
 * it went and no way back. A toast both reports the destination and offers the
 * reversal, which is far more useful than a confirmation dialog before the fact
 * (people dismiss those without reading, and they punish the common case to
 * guard the rare one).
 *
 * Kept deliberately small: no queueing library, no portals. Toasts stack in a
 * corner and expire on a timer.
 */

export interface Toast {
  id: number;
  message: string;
  /** Shown as an "Undo" button; the toast closes once it resolves. */
  onUndo?: () => Promise<unknown> | void;
  tone?: 'default' | 'error';
}

interface ToastApi {
  push: (t: Omit<Toast, 'id'>) => void;
}

const ToastContext = createContext<ToastApi>({ push: () => {} });

/** Raise a toast from anywhere under the shell. */
export function useToast(): ToastApi {
  return useContext(ToastContext);
}

/**
 * How long a toast lingers. Long enough to read a filename and decide, short
 * enough not to pile up during a burst of moves.
 */
const TTL_MS = 10_000;

/** Countdown tick. 100ms so the ring animates smoothly rather than stepping. */
const TICK_MS = 100;

export function ToastHost({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = nextId.current++;
    setToasts((cur) => [...cur, { ...t, id }]);
  }, []);

  const api = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 left-1/2 z-50 flex w-[min(92vw,26rem)] -translate-x-1/2 flex-col gap-2"
        // Announced politely: a move is a confirmation, not an alert that
        // should interrupt whatever the user is reading.
        role="status"
        aria-live="polite"
      >
        {toasts.map((t) => (
          <ToastRow key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastRow({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const [undoing, setUndoing] = useState(false);
  const [remaining, setRemaining] = useState(TTL_MS);
  // Hovering pauses the countdown: reading the filename to decide whether to
  // undo should not cost you the chance to.
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    // Undo in flight, or the pointer is on the toast: hold it open.
    if (undoing || hovered) return;
    const started = Date.now();
    const from = remaining;
    const tick = setInterval(() => {
      const left = from - (Date.now() - started);
      if (left <= 0) {
        clearInterval(tick);
        onDismiss();
      } else {
        setRemaining(left);
      }
    }, TICK_MS);
    return () => clearInterval(tick);
    // `remaining` is intentionally not a dependency — it changes every tick and
    // would restart the interval each time. It is read once as the starting
    // point when the timer (re)arms after a pause.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [undoing, hovered, onDismiss]);

  const seconds = Math.ceil(remaining / 1000);

  async function undo() {
    if (!toast.onUndo) return;
    setUndoing(true);
    try {
      await toast.onUndo();
      onDismiss();
    } finally {
      setUndoing(false);
    }
  }

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      // Keyboard users get the same reprieve: tabbing to Undo pauses the timer.
      onFocusCapture={() => setHovered(true)}
      onBlurCapture={() => setHovered(false)}
      className={cn(
        'pointer-events-auto relative flex items-center gap-3 overflow-hidden rounded-lg px-3 py-2 text-[13px] shadow-lg',
        toast.tone === 'error'
          ? 'bg-red-600 text-white'
          : 'surface-card border line text-strong',
      )}
    >
      {/* Draining progress bar along the bottom edge — the countdown at a
          glance, without having to read the number. */}
      {toast.onUndo && !undoing && (
        <span
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-0.5 origin-left bg-brand-500/60"
          style={{ transform: `scaleX(${Math.max(0, remaining / TTL_MS)})` }}
        />
      )}
      {toast.tone !== 'error' && <Check className="size-4 shrink-0 text-brand-500" />}
      <span className="min-w-0 flex-1 truncate" title={toast.message}>{toast.message}</span>
      {toast.onUndo && (
        <button
          onClick={() => void undo()}
          disabled={undoing}
          className="flex shrink-0 items-center gap-1 rounded px-2 py-1 font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50 dark:text-brand-300 dark:hover:bg-white/10"
        >
          <Undo2 className="size-3.5" />
          {undoing ? t('toasts.undoing') : t('toasts.undo')}
          {/* The seconds left, so the deadline is explicit rather than implied
              by a bar. Hidden while paused — the number would sit frozen and
              read as broken. */}
          {!undoing && !hovered && (
            <span className="tabular-nums text-[11px] text-muted">{seconds}s</span>
          )}
        </button>
      )}
      <button
        onClick={onDismiss}
        className="shrink-0 rounded p-1 text-muted hover:bg-black/5 dark:hover:bg-white/10"
        aria-label={t('toasts.dismiss')}
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
